/**
 * Instructions overlay for the Transliteration Evaluation page - Markdown version
 */

import React from 'react'
import { FullPageOverlay, Row, Button, MarginBox } from '@fs/zion-ui'
import type { UseOverlayReturnOptions } from '@fs/zion-ui'
import { Global } from '@emotion/core'
import Markdown from 'markdown-to-jsx'
import { useTranslation } from 'react-i18next'
import { Link } from '@fs/zion-router'
import { createMarkdownStyles } from '../../../../assets/markdownStyles'
import { instructionsMarkdown } from './instructionsContent'

const markdownStyles = createMarkdownStyles('transliteration-instructions')

interface MarkdownLinkProps {
  href?: string
  children?: React.ReactNode
  [key: string]: unknown
}

// Custom link component that uses React Router for internal links
const MarkdownLink = ({ href, children, ...props }: MarkdownLinkProps): JSX.Element => {
  // If it's an internal link (starts with /), use React Router
  if (href?.startsWith('/')) {
    return (
      <Link to={href} {...props}>
        {children}
      </Link>
    )
  }
  // External links use normal anchor tags
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
      {children}
    </a>
  )
}

interface MarkdownInstructionsOverlayProps {
  overlay: UseOverlayReturnOptions
}

export const MarkdownInstructionsOverlay: React.FC<MarkdownInstructionsOverlayProps> = ({ overlay }) => {
  const [t] = useTranslation()

  return (
    <FullPageOverlay
      {...overlay}
      title={t('transliteration-eval-page.instructions-overlay.title', 'How to Use Transliteration Ranking')}
      footer={
        <Row alignX="end">
          <Button onClick={overlay.handleClick} emphasis="high">
            {t('transliteration-eval-page.instructions-overlay.close.button', 'Close')}
          </Button>
        </Row>
      }
    >
      <Global styles={markdownStyles} />
      <MarginBox marginX="md" marginY="md">
        <div className="transliteration-instructions">
          <Markdown options={{ overrides: { a: MarkdownLink } }}>{instructionsMarkdown}</Markdown>
        </div>
      </MarginBox>
    </FullPageOverlay>
  )
}
